<template>
  <div class="setup-step-3">
    <Card flat>
      <template #title>
        <v-skeleton-loader
          v-if="pageLoading"
          type="heading"
          width="50%"
          height="28px"
          class="mt-1 mb-1"
        />
        <h5
          v-else
          class="mt-2 mb-2"
        >
          Mandatory SSO
        </h5>
      </template>

      <template #text>
        <v-skeleton-loader
          v-if="pageLoading"
          type="text"
          class="mt-6"
          height="38px"
        />
        <DpplToggleButton
          v-else
          v-model="forced"
          :disabled="!currentSsoNameMatch"
          :label="forced ? 'Enabled' : 'Disabled'"
        />

        <v-skeleton-loader
          v-if="pageLoading"
          type="paragraph"
          class="mt-1"
          height="56px"
        />
        <div v-else>
          <div v-if="!isForced && !currentSsoNameMatch">
            Please login on your
            <a
              :href="`/login/${name}`"
              @click="onSsoLoginClick"
            >
              SSO Login Page
            </a>
            to enable this toggle.
          </div>

          <div v-if="isForced">
            Turn the toggle off to unlock the form above.
          </div>

          <div v-if="currentSsoNameMatch">
            The following emails will be redirected to your SSO login page
            if the toggle is turned on.
            <span
              v-for="(domain, index) in domains"
              :key="domain"
            >
              <template v-if="index > 0">,</template>
              user<strong>@{{ domain }}</strong>
            </span>
          </div>
        </div>

        <div class="mt-6">
          <v-skeleton-loader
            v-if="pageLoading"
            type="button"
            height="36px"
          />
          <v-btn
            v-else
            color="error"
            variant="outlined"
            rounded="lg"
            :disabled="isForced"
            @click="showDeleteModal = true"
          >
            Remove SSO Setup
          </v-btn>

          <Modal
            :show="showDeleteModal"
            action-text="Remove SSO"
            cancel-text=""
            :button-props="{ color: 'error', variant: 'outlined' }"
            @action="onRemoveSso"
            @close="showDeleteModal = false"
          >
            <template #title>
              Remove Single Sign On (SSO)
            </template>

            <template #content>
              <div>
                Removing SSO will permanently delete all related
                configurations and details.
              </div>

              <div class="mt-4 text-textDarkest">
                Please note: This action cannot be undone.
              </div>
            </template>
          </Modal>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import { mapState, mapActions } from 'pinia';
import { useAppLoading } from '@/stores/app/loading';
import { useAppAuthData } from '@/stores/app/authData';
import { useSettingsSsoSetup } from '@/stores/settings/ssoSetup';
import DpplToggleButton from '@/components/datapeople/DpplToggleButton.vue';
import Modal from '@/components/shared/Modal.vue';
import Card from './Card.vue';

export default {
  name: 'SetupStep3',
  components: { Card, DpplToggleButton, Modal },

  data() {
    return { showDeleteModal: false };
  },

  computed: {
    ...mapState(useAppLoading, ['pageLoading']),
    ...mapState(useSettingsSsoSetup, ['isForced', 'name', 'domains']),

    ...mapState(useAppAuthData, ['authData']),
    currentSsoNameMatch() { return this.authData.ssoName === this.name; },

    forced: {
      get() {
        return this.isForced;
      },
      set(isForced) {
        this.update({ isForced: Boolean(isForced) });
      },
    },
  },

  methods: {
    ...mapActions(useSettingsSsoSetup, ['update']),

    onRemoveSso() {
      this.showDeleteModal = false;
      this.update({ isActive: false });
    },

    onSsoLoginClick(event) {
      event.preventDefault();
      window.sessionStorage.setItem('redirectRoute', JSON.stringify({ name: 'sso-setup' }));
      window.location.href = `/login/${this.name}`;
    },
  },
};
</script>

<style scoped lang="less">
:deep(.v-input.v-switch) {
  display: flex;
  padding: 10px 0;
}

:deep(.v-input .v-label) {
  max-width: initial;
}
</style>
